import {
  Component,
  OnInit,
  Signal,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { whitespaceNameValidator } from './whitespace-name.validator';

import { Observable } from 'rxjs';
import { injectSyrfDialogRef } from '@shared/dialog/syrf-dialog.service';
import { SyrfDialogLayoutComponent } from '@shared/dialog/syrf-dialog-layout.component';
import { SyrfDialogCloseDirective } from '@shared/dialog/syrf-dialog-close.directive';

import { selectCurrentUser } from '@core/auth/auth.selectors';
import { IEditProjectVm } from '@shared/dialogs/edit-project-modal/edit-project.viewmodel';
import { ISimpleOption } from '@core/state/entities/annotation-question/annotation-question.entity';
import { selectKeywordsAsSimpleOptions } from '@core/state/ui/project-index/project-index-ui.selectors';
import { IUser } from '@core/auth/user.entity';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { selectProjectIsLoading } from '@core/state/ui/project/project-ui.selectors';
import { ProjectNameValidator } from '@app/project-index/dialogs/create-project-wizard/project-name.validator';
import { ProjectHttpService } from '@core/services/api-client.generated';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import { ExtendedModule } from '@ngbracket/ngx-layout/extended';
import { NgClass, AsyncPipe, KeyValuePipe } from '@angular/common';
import { ProjectSetupComponent } from '../../../project/project-overview/project-setup/project-setup.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { InformationBoxComponent } from '../../../shared/information-box/information-box.component';
import { AutocompleteChiplistComponent } from '../../../shared/form-controls/autocomplete-chiplist/autocomplete-chiplist.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';

export interface ICreateProjectFormData {
  basic: {
    name: string;
    protocolLink: string;
    description: string;
    contactEmail: string;
    isPrivate: boolean;
    keywords: string[];
  };
  screening: {
    inclusionCriteria: string;
    exclusionCriteria: string;
  };
  livingSearch?: {
    livingSearch: boolean;
    searchString: string;
    searchEngine: string;
  };
  machineLearning?: {
    machineLearning: boolean;
  };
}

export const convertToProjectVm = (
  formData: ICreateProjectFormData
): IEditProjectVm => ({
  livingSearch: false,
  searchString: '',
  searchEngine: '',
  ...formData.basic,
  ...formData.screening,
  ...formData.livingSearch,
});

/** The heading openers pass as the dialog title (the heading the dialog always showed). */
export const CREATE_PROJECT_WIZARD_TITLE = 'Create Project';

/**
 * The multi-step create project wizard. Each step keeps its own actions row inside the stepper. Results: Cancel,
 * Escape and the backdrop `undefined` (Cancel closed with `''` before #4599; the only opener ignores the result);
 * dismissing the final setup step closes with `undefined`.
 */
@Component({
  selector: 'app-create-project-wizard',
  templateUrl: './create-project-wizard.component.html',
  styleUrls: ['./create-project-wizard.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SyrfDialogLayoutComponent,
    SyrfDialogCloseDirective,
    FormsModule,
    ReactiveFormsModule,
    MatStepperModule,
    FlexLayoutModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    AutocompleteChiplistComponent,
    InformationBoxComponent,
    MatCheckboxModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatSelectModule,
    MatOptionModule,
    MatProgressSpinnerModule,
    ProjectSetupComponent,
    NgClass,
    AsyncPipe,
    KeyValuePipe,
  ],
})
export class CreateProjectWizardComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper;
  currentUser: Signal<IUser | null>;
  createProjectForm: UntypedFormGroup;
  keywordOptions$: Observable<ISimpleOption[]>;
  projectLoading$: Observable<boolean>;
  ProjectNameValidator = ProjectNameValidator;

  private readonly _fb = inject(UntypedFormBuilder);
  private readonly _store = inject(Store);
  private readonly _dialogRef = injectSyrfDialogRef<CreateProjectWizardComponent>();
  private readonly _projectService = inject(ProjectHttpService);

  constructor() {
    this._store.dispatch(projectDetailActions.loadKeywords());
    this.currentUser = this._store.selectSignal(selectCurrentUser);
    this.keywordOptions$ = this._store.select(selectKeywordsAsSimpleOptions);
    this.projectLoading$ = this._store.select(selectProjectIsLoading);

    this.createProjectForm = this._fb.group({
      basic: this._fb.group({
        name: new UntypedFormControl('', {
          validators: [Validators.required, whitespaceNameValidator],
          asyncValidators: ProjectNameValidator.createValidator(
            this._projectService,
            this._store
          ),
          updateOn: 'change',
        }),
        protocolLink: '',
        description: '',
        contactEmail: [
          this.currentUser() ? this.currentUser().email || '' : '',
          Validators.required,
        ],
        keywords: [[]],
        isPrivate: true,
      }),
      screening: this._fb.group({
        inclusionCriteria: '',
        exclusionCriteria: '',
      }),
      livingSearch: this._fb.group({
        livingSearch: false,
        searchString: '',
        searchEngine: 'pubmed',
      }),
      machineLearning: this._fb.group({
        machineLearning: false,
      }),
    });
    (this.createProjectForm.get('livingSearch') as UntypedFormGroup).disable();
    (
      this.createProjectForm.get('machineLearning') as UntypedFormGroup
    ).disable();
  }

  createProject() {
    const formValue: ICreateProjectFormData = this.createProjectForm.value;
    this._store.dispatch(
      projectDetailActions.create({ createVm: convertToProjectVm(formValue) })
    );
  }

  ngOnInit() {}

  onDismissDialog() {
    try {
      this._dialogRef?.close();
    } catch (e) {
      console.warn('Caught close error', e);
    }
  }
}
